/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import { CheckCircleOutlined, CloseOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import useWindowSize from 'src/hooks/useWindowSize'
import Text from 'src/components/ts-components/Text'

import BaseActionButton from '../../components/Page/BaseActionButton'
import { playoffsIds } from '@sportninja/common/constants/playoffTypes'
import CloneCompetitionModal from './CloneCompetitionModal'

const SECONDS_BEFORE_REDIRECT = 10
const SECONDS_BEFORE_REDIRECT_MS = SECONDS_BEFORE_REDIRECT * 1000

/**
 * Modern success modal component for clone competition
 */
const CloneSuccessModal = ({
  isOpen,
  onClose,
  clonedSchedule,
  countdown,
  onGoNow,
}) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])

  /**
   * Handles modal close and cleanup
   */
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  /**
   * Renders the close icon
   */
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile ? '100vh' : 'auto'} !important;
        margin: ${isMobile ? '0' : '24px'};
        padding: 0;
        max-width: ${isMobile ? '100%' : '480px'};
        top: ${isMobile ? '0' : '50%'};
        .ant-modal-content {
          height: ${isMobile ? '100vh' : 'auto'} !important;
          border-radius: ${isMobile ? '0' : '16px'};
          display: flex;
          flex-direction: column;
          background: ${colors.SECONDARY_300};
          border: 1px solid ${colors.SECONDARY_100};
        }
        .ant-modal-body {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          padding: 24px;
        }
        .ant-modal-header {
          display: none;
        }
      `}
      width={isMobile ? '100%' : 480}
      closeIcon={closeIcon()}
      open={isOpen}
      onCancel={handleClose}
      centered={!isMobile}
      maskClosable={false}
      footer={null}
      styles={{
        content: {
          backgroundColor: colors.SECONDARY_300,
          padding: 0,
        },
        wrapper: {
          zIndex: 10002,
        },
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
          zIndex: 10001,
        },
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          gap: 16px;
          position: relative;
        `}
      >
        {/* Success Icon */}
        <CheckCircleOutlined
          css={css`
            font-size: 48px;
            color: ${colors.SUCCESS_200};
          `}
        />

        {/* Title */}
        <Text variant='heading1' weight='semiBold' color={colors.WHITE}>
          Competition Cloned Successfully
        </Text>

        {/* Competition Details */}
        <div
          css={css`
            align-self: stretch;
            border: 1px solid ${colors.SECONDARY_100};
            border-radius: 8px;
            padding: 16px;
            background: ${colors.SECONDARY_200};
          `}
        >
          <Text variant='heading4' weight='semiBold' color={colors.WHITE}>
            {clonedSchedule?.name_full}
          </Text>
          <Text
            variant='paragraphMedium'
            weight='regular'
            color={colors.NEUTRAL_30}
          >
            {dayjs(clonedSchedule?.starts_at).utc().format('MMM D, YYYY')} -{' '}
            {dayjs(clonedSchedule?.ends_at).utc().format('MMM D, YYYY')}
          </Text>
        </div>

        {/* Redirect Message */}
        <div
          css={css`
            align-self: stretch;
            text-align: center;
          `}
        >
          <Text variant='paragraphMedium' weight='regular' color={colors.WHITE}>
            Redirecting to your new {t('common:competition')} in{' '}
            <span
              css={css`
                font-weight: 700;
                color: ${colors.PRIMARY};
              `}
            >
              {countdown}
            </span>{' '}
            seconds...
          </Text>
        </div>

        {/* Divider */}
        <div
          css={css`
            align-self: stretch;
            height: 1px;
            background: ${colors.SECONDARY_100};
          `}
        />

        {/* Action Buttons */}
        <div
          css={css`
            align-self: stretch;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            display: inline-flex;
          `}
        >
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              background: ${colors.PRIMARY};
              border-radius: 8px;
              border: 2px solid ${colors.PRIMARY};
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: pointer;
              transition: all 0.2s;
              &:hover {
                opacity: 0.8;
              }
            `}
            onClick={onGoNow}
          >
            <Text variant='heading6' weight='semiBold' color={colors.WHITE}>
              GO NOW
            </Text>
          </button>
          <button
            css={css`
              flex: 1 1 0;
              padding: 8px 24px;
              border-radius: 8px;
              border: 2px solid ${colors.WHITE};
              background: transparent;
              justify-content: center;
              align-items: center;
              gap: 8px;
              display: flex;
              cursor: pointer;
              transition: all 0.2s;
              &:hover {
                background: rgba(255, 255, 255, 0.1);
              }
            `}
            onClick={handleClose}
          >
            <Text variant='heading6' weight='semiBold' color={colors.WHITE}>
              DISMISS
            </Text>
          </button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Transforms a given bracket template id string to its corresponding id value from an object of ids.
 *
 * @param {string} bracketTemplateAsString - The bracket template id string to transform.
 * @param {Object.<string, *>} ids - The object of ids to search for a matching id value.
 * @returns {number|null} - The corresponding id value if the id was found, otherwise returns null.
 */
const transformBracketTemplateId = (bracketTemplateAsString, ids) => {
  if (!bracketTemplateAsString || !ids) {
    return null
  }
  if (typeof bracketTemplateAsString !== 'string') {
    return null
  }
  if (typeof ids !== 'object') {
    return null
  }
  if (!ids[bracketTemplateAsString]) {
    return null
  }
  return ids[bracketTemplateAsString]
}

// Open an info modal, that displays info about the new cloned competition, but
// also informs the user they will be redirected to that competition in 5 seconds
// Also, give them an option to dismiss the modal and stay where they are
const CloneSchedule = ({
  Button,
  schedule,
  scheduleId,
  onToggle = () => {},
  shouldEnablePlayoffs,
}) => {
  const history = useHistory()

  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const timeout = useRef(null)
  const interval = useRef(null)
  const [countdown, setCountdown] = useState(SECONDS_BEFORE_REDIRECT)
  const [clonedSchedule, setClonedSchedule] = useState({})

  useEffect(() => {
    if (!isSuccessModalOpen) {
      clearTimeout(timeout.current)
      clearInterval(interval.current)
      setClonedSchedule({})
      setCountdown(SECONDS_BEFORE_REDIRECT)
    }
  }, [isSuccessModalOpen])

  const goToSchedule = (comp) => {
    // If the new competition is a tournament, send user to game slots instead
    history.push(
      `${ROUTES.SCHEDULE_ROOT}/${comp.id}/${
        comp.is_tournament ? 'gameslots' : 'setup'
      }`
    )
  }

  /**
   * Handle clone form submission
   */
  const handleCloneSubmit = async (formData) => {
    try {
      setIsLoading(true)

      // Transform form data to match API expectations
      const body = {
        name_full: formData.name_full,
        abbreviation: formData.abbreviation,
        starts_at: formData.starts_at,
        ends_at: formData.ends_at,
        is_tournament: formData.is_tournament,
        is_registration_schedule: formData.is_registration_schedule,
        name: formData.name_full, // API expects both name and name_full
      }

      if (body.is_tournament) {
        // Add bracket template logic if needed
        body.bracket_template_id = transformBracketTemplateId(
          body.bracket_template_id,
          playoffsIds
        )
        if (body.bracket_template_id === null) {
          delete body.bracket_template_id
        }
      } else {
        delete body.bracket_template_id
      }

      const response = await req(`/schedules/${scheduleId}/clone`, {
        method: 'POST',
        body: JSON.stringify(body),
      })

      setClonedSchedule(response.data)
      setIsCloneModalOpen(false)
      setIsSuccessModalOpen(true)

      timeout.current = setTimeout(() => {
        goToSchedule(response.data)
      }, SECONDS_BEFORE_REDIRECT_MS)

      interval.current = setInterval(() => {
        setCountdown((s) => {
          return s === 0 ? 0 : --s
        })
      }, 1000)
    } catch (error) {
      console.error('Error cloning schedule:', error)
      throw error // Let the modal handle the error
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handle opening the clone modal
   */
  const handleOpenCloneModal = () => {
    setIsCloneModalOpen(true)
    onToggle()
  }

  return (
    <>
      {/* Clone Button */}
      {Button ? (
        <Button onClick={handleOpenCloneModal} onToggle={onToggle} />
      ) : (
        <BaseActionButton
          title={t('Web:cloneCompetition')}
          name='clone'
          onClick={handleOpenCloneModal}
        />
      )}

      {/* Clone Competition Modal */}
      <CloneCompetitionModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        onSubmit={handleCloneSubmit}
        schedule={schedule}
        isLoading={isLoading}
      />

      {/* Success Modal */}
      <CloneSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        clonedSchedule={clonedSchedule}
        countdown={countdown}
        onGoNow={() => {
          clearTimeout(timeout.current)
          setIsSuccessModalOpen(false)
          goToSchedule(clonedSchedule)
        }}
      />
    </>
  )
}

export default CloneSchedule
